import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import api from '@/api/knowledgeBase.service';
import { ACTIONS, MODULES } from '@/constants/pemissions';
import { ButtonsCollection, PermissionElement } from '@/ui-kit';
import { useKnowledgeBaseContext } from '@/views/knowledgeBase/context/useKnowledgeBaseContext';
import './style.sass'

const SortableSubcategoryItem = ({
  subcategory,
  onSubcategoryClick,
  onEditSubcategory,
  onDeleteSubcategory,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: subcategory.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      key={`subcategory-item-${subcategory?.id}`}
      className="subcategory-item"
      onClick={() => onSubcategoryClick(subcategory?.id)}
    >
      <PermissionElement module={MODULES.KNOWLEDGE_BASE} action={ACTIONS.UPDATE}>
        <span
          {...listeners}
          className="subcategory-item__controls__drag-handle icon-settings-vertical"
        >
          <span className="path1" />
          <span className="path2" />
          <span className="path3" />
        </span>
      </PermissionElement>
      <h4 className="subcategory-item__title">{subcategory?.description}</h4>
      <div className="subcategory-item__controls">
        <PermissionElement module={MODULES.KNOWLEDGE_BASE} action={ACTIONS.CREATE}>
          <ButtonsCollection.ButtonIcons
            onClick={(e) => onEditSubcategory(e, subcategory.id)}
            title="Edit subcategory"
            className="subcategory-item__controls__edit-button"
          >
            <span className="icon-edit-icon" />
          </ButtonsCollection.ButtonIcons>
        </PermissionElement>
        <PermissionElement module={MODULES.KNOWLEDGE_BASE} action={ACTIONS.DELETE}>
          <ButtonsCollection.ButtonIcons
            onClick={(e) => onDeleteSubcategory(e, subcategory.id)}
            title="Delete subcategory"
            className="subcategory-item__controls__delete-button"
          >
            <span className="icon-trash" />
          </ButtonsCollection.ButtonIcons>
        </PermissionElement>
      </div>
    </div>
  );
};

export const CategoryItem = ({
 category,
 onOpenEditCategoryModal,
 onOpenDeleteCategoryModal,
 onOpenCreateSubcategoryModal,
 onOpenEditSubcategoryModal,
 onOpenDeleteSubcategoryModal,
}) => {
  const { t } = useTranslation('common');

  const { push } = useHistory();

  const { setCategoryId, setParentId, refreshCategories } = useKnowledgeBaseContext();

  const [isOpenList, setIsOpenList] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = async (event) => {
    if (!event.over) return;

    await api.swapKnowledgeBaseArticles(event.active.id, event.over.id);

    await refreshCategories();
  };

  const {
    attributes: categoryAttributes,
    listeners: categoryListeners,
    setNodeRef: setCategoryNodeRef,
    transform: categoryTransform,
    transition: categoryTransition,
  } = useSortable({
    id: category.id,
  });

  const categoryStyle = {
    transform: CSS.Transform.toString(categoryTransform),
    transition: categoryTransition,
  };

  const onOpenCategoryItem = useCallback(() => {
    setIsOpenList(prevState => !prevState);
  }, []);

  const onCreateSubcategory = useCallback((e) => {
    e.stopPropagation();

    setParentId(category?.id);

    onOpenCreateSubcategoryModal();
  }, []);

  const onEditSubcategory = useCallback((e, subcategoryId) => {
    e.stopPropagation();

    setCategoryId(subcategoryId);

    onOpenEditSubcategoryModal();
  }, [onOpenEditSubcategoryModal, setCategoryId]);

  const onEditCategory = useCallback((e) => {
    e.stopPropagation();

    setCategoryId(category?.id);

    onOpenEditCategoryModal();
  }, [onOpenEditCategoryModal, setCategoryId]);

  const onDeleteCategory = useCallback((e) => {
    e.stopPropagation();

    setCategoryId(category?.id);

    onOpenDeleteCategoryModal();
  }, [onOpenDeleteCategoryModal, setCategoryId]);

  const onDeleteSubcategory = useCallback((e, id) => {
    e.stopPropagation();

    setCategoryId(id);

    onOpenDeleteSubcategoryModal();
  }, [onOpenDeleteSubcategoryModal, setCategoryId]);

  const onSubcategoryClick = useCallback((id) => {
    push(`/knowledge-base/subcategory/${id}`);
  }, [])

  return (
    <div
      ref={setCategoryNodeRef}
      style={categoryStyle}
      className={classNames('category-item', isOpenList && 'category-item--open')}
    >
      <div className="category-item__info-header" onClick={() => onOpenCategoryItem()}>
        <PermissionElement module={MODULES.KNOWLEDGE_BASE} action={ACTIONS.UPDATE}>
          <span
            {...categoryAttributes}
            {...categoryListeners}
            className="subcategory-item__controls__drag-handle icon-settings-vertical"
          >
            <span className="path1" />
            <span className="path2" />
            <span className="path3" />
          </span>
        </PermissionElement>
        <ButtonsCollection.ButtonIcons
          className="category-item__info-header__toggle-button"
          title={`${!isOpenList ? t('Show more') : t('Show less')}`}
        >
          <span className="icon-expand-arrow-dn" />
        </ButtonsCollection.ButtonIcons>
        <h4 className="category-item__info-header__title">{category.description}</h4>
          <div className='category-item__info-header__controls'>
            <PermissionElement
              module={MODULES.KNOWLEDGE_BASE}
              action={ACTIONS.CREATE}
            >
              <div className='category-item__info-header__controls__create-button'>
                <ButtonsCollection.ButtonPlus
                  onClick={onCreateSubcategory}
                  title='Create subcategory'
                  className='category-item__info-header__controls__create-button__button-plus'
                />
              </div>
            </PermissionElement>
            <PermissionElement
              module={MODULES.KNOWLEDGE_BASE}
              action={ACTIONS.UPDATE}
            >
              <ButtonsCollection.ButtonIcons
                onClick={onEditCategory}
                title='Edit category'
                className='category-item__info-header__controls__edit-button'
              >
                <span className='icon-edit-icon' />
              </ButtonsCollection.ButtonIcons>
            </PermissionElement>
            <PermissionElement
              module={MODULES.KNOWLEDGE_BASE}
              action={ACTIONS.DELETE}
            >
              <ButtonsCollection.ButtonIcons
                onClick={onDeleteCategory}
                title='Delete category'
                className='category-item__info-header__controls__delete-button'
              >
                <span className='icon-trash' />
              </ButtonsCollection.ButtonIcons>
            </PermissionElement>
          </div>
      </div>
      <div className="category-item__subcategories-list">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={category.subCategories?.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {category?.subCategories?.map((subcategory) => (
              <SortableSubcategoryItem
                key={subcategory.id}
                subcategory={subcategory}
                onSubcategoryClick={onSubcategoryClick}
                onEditSubcategory={onEditSubcategory}
                onDeleteSubcategory={onDeleteSubcategory}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
